import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { User, Package, LogOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, logout, checkAuth, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // Check auth on mount if user data was passed from AuthCallback
  useEffect(() => {
    if (location.state?.user && !user) {
      checkAuth();
    }
  }, [location.state, user, checkAuth]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [authLoading, user, navigate]);

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;
      
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/orders`, {
          headers,
          withCredentials: true
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [user, token]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-600';
      case 'pending':
        return 'bg-yellow-600';
      case 'cancelled':
        return 'bg-red-600';
      default:
        return 'bg-[#27272A]';
    }
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="dashboard-page">
      {/* Header */}
      <section className="py-12 border-b border-[#27272A]">
        <div className="container-main">
          <div className="flex items-center gap-6">
            <Avatar className="w-20 h-20 border-2 border-[#C41E3A]">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-[#C41E3A] text-white font-barlow font-bold text-2xl">
                {user.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="heading-display text-3xl md:text-4xl text-white" data-testid="user-name">
                {user.name}
              </h1>
              <p className="text-white/60" data-testid="user-email">{user.email}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Content */}
      <section className="section-gap">
        <div className="container-main">
          <Tabs defaultValue="orders" className="w-full">
            <TabsList className="flex gap-4 mb-8 bg-transparent">
              <TabsTrigger
                value="orders"
                className="font-barlow font-bold uppercase data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white bg-transparent border border-[#27272A] text-white/60 px-6 py-3"
                data-testid="orders-tab"
              >
                <Package className="w-5 h-5 mr-2" />
                Orders
              </TabsTrigger>
              <TabsTrigger
                value="profile"
                className="font-barlow font-bold uppercase data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white bg-transparent border border-[#27272A] text-white/60 px-6 py-3"
                data-testid="profile-tab"
              >
                <User className="w-5 h-5 mr-2" />
                Profile
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders">
              {ordersLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
                </div>
              ) : orders.length > 0 ? (
                <div className="space-y-4" data-testid="orders-list">
                  {orders.map((order) => (
                    <div
                      key={order.order_id}
                      className="bg-[#0A0A0A] border border-[#27272A] p-6"
                      data-testid={`order-${order.order_id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <p className="label-caps text-white/50 mb-1">Order ID</p>
                          <p className="font-barlow font-bold text-white">{order.order_id}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={`${getStatusColor(order.status)} text-white font-barlow uppercase text-xs px-3 py-1`}>
                            {order.status}
                          </Badge>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {order.items?.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-white/70">
                              {item.name} x{item.quantity}
                            </span>
                            <span className="text-white">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t border-[#27272A]">
                        <span className="text-white/60 text-sm">
                          {new Date(order.created_at).toLocaleDateString()}
                        </span>
                        <span className="font-barlow font-black text-lg text-white">
                          ${order.total?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12" data-testid="no-orders">
                  <Package className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <h3 className="heading-display text-xl text-white mb-2">No Orders Yet</h3>
                  <p className="text-white/60 mb-6">Start shopping to see your orders here.</p>
                  <Link to="/products">
                    <Button className="btn-primary">Browse Products</Button>
                  </Link>
                </div>
              )}
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="bg-[#0A0A0A] border border-[#27272A] p-6 max-w-xl" data-testid="profile-section">
                <h3 className="font-barlow font-bold text-xl text-white mb-6">Account Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="label-caps text-white/50 mb-1">Name</p>
                    <p className="text-white">{user.name}</p>
                  </div>
                  <div>
                    <p className="label-caps text-white/50 mb-1">Email</p>
                    <p className="text-white">{user.email}</p>
                  </div>
                  <div>
                    <p className="label-caps text-white/50 mb-1">Member ID</p>
                    <p className="text-white/60 text-sm">{user.user_id}</p>
                  </div>
                </div>

                <Button
                  onClick={handleLogout}
                  className="btn-secondary mt-8 flex items-center gap-2"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
